import { Box, Button, Skeleton, TextField, Typography } from '@mui/material';

import { useT } from '@/hooks';

interface SharesInputProps {
  shares: string;
  activeTab: 'buy' | 'sell';
  isAuthenticated: boolean;
  userHolding: number;
  sharesNum: number;
  isLoadingHolding?: boolean;
  onSharesChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onQuickAmount: (amount: number) => void;
  onSetShares: (value: string) => void;
}

export function SharesInput({
  shares,
  activeTab,
  isAuthenticated,
  userHolding,
  sharesNum,
  isLoadingHolding = false,
  onSharesChange,
  onQuickAmount,
  onSetShares,
}: SharesInputProps) {
  const { t } = useT();
  return (
    <Box sx={{ mb: 2.5 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography
          sx={{
            fontSize: '0.875rem',
            fontWeight: 600,
            color: 'text.primary',
          }}
        >
          {t('trade.shares', { defaultValue: 'Shares' })}
        </Typography>
        {activeTab === 'sell' && isAuthenticated && (
          <Typography
            sx={{
              fontSize: '0.75rem',
              fontWeight: 500,
              color: 'text.secondary',
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            {t('trade.availableShares', { defaultValue: 'Available Shares:' })}{' '}
            {isLoadingHolding ? (
              <Skeleton
                variant="text"
                width={48}
                height={16}
                sx={{ display: 'inline-block', borderRadius: '4px' }}
              />
            ) : (
              userHolding.toLocaleString(undefined, {
                minimumFractionDigits: 0,
                maximumFractionDigits: 6,
              })
            )}
          </Typography>
        )}
      </Box>
      <TextField
        fullWidth
        size="small"
        value={shares}
        onChange={onSharesChange}
        sx={{
          mb: activeTab === 'sell' && sharesNum > 0 ? 0.75 : 1.5,
          '& input': {
            textAlign: 'center',
            fontWeight: 600,
            fontSize: '0.875rem',
            py: 1,
          },
        }}
      />
      {activeTab === 'buy' ? (
        <Box sx={{ display: 'flex', gap: 0.75 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => onQuickAmount(-100)}
            sx={{
              flex: 1,
              fontWeight: 600,
              fontSize: '0.75rem',
              py: 0.75,
              borderColor: 'divider',
              color: 'text.secondary',
              borderRadius: '6px',
              textTransform: 'none',
              '&:hover': {
                borderColor: 'primary.main',
                bgcolor: 'action.hover',
              },
            }}
          >
            -100
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={() => onQuickAmount(-10)}
            sx={{
              flex: 1,
              fontWeight: 600,
              fontSize: '0.75rem',
              py: 0.75,
              borderColor: 'divider',
              color: 'text.secondary',
              borderRadius: '6px',
              textTransform: 'none',
              '&:hover': {
                borderColor: 'primary.main',
                bgcolor: 'action.hover',
              },
            }}
          >
            -10
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={() => onQuickAmount(10)}
            sx={{
              flex: 1,
              fontWeight: 600,
              fontSize: '0.75rem',
              py: 0.75,
              borderColor: 'divider',
              color: 'text.secondary',
              borderRadius: '6px',
              textTransform: 'none',
              '&:hover': {
                borderColor: 'primary.main',
                bgcolor: 'action.hover',
              },
            }}
          >
            +10
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={() => onQuickAmount(100)}
            sx={{
              flex: 1,
              fontWeight: 600,
              fontSize: '0.75rem',
              py: 0.75,
              borderColor: 'divider',
              color: 'text.secondary',
              borderRadius: '6px',
              textTransform: 'none',
              '&:hover': {
                borderColor: 'primary.main',
                bgcolor: 'action.hover',
              },
            }}
          >
            +100
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={() => onQuickAmount(200)}
            sx={{
              flex: 1,
              fontWeight: 600,
              fontSize: '0.75rem',
              py: 0.75,
              borderColor: 'divider',
              color: '#9333ea',
              borderRadius: '6px',
              textTransform: 'none',
              '&:hover': {
                borderColor: '#9333ea',
                bgcolor: 'action.hover',
              },
            }}
          >
            +200
          </Button>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', gap: 0.75 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              const val = userHolding * 0.25;
              onSetShares(val.toString());
            }}
            sx={{
              flex: 1,
              fontWeight: 600,
              fontSize: '0.75rem',
              py: 0.75,
              borderColor: 'divider',
              color: 'text.secondary',
              borderRadius: '6px',
              textTransform: 'none',
              '&:hover': {
                borderColor: 'primary.main',
                bgcolor: 'action.hover',
              },
            }}
          >
            25%
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              const val = userHolding * 0.5;
              onSetShares(val.toString());
            }}
            sx={{
              flex: 1,
              fontWeight: 600,
              fontSize: '0.75rem',
              py: 0.75,
              borderColor: 'divider',
              color: 'text.secondary',
              borderRadius: '6px',
              textTransform: 'none',
              '&:hover': {
                borderColor: 'primary.main',
                bgcolor: 'action.hover',
              },
            }}
          >
            50%
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              onSetShares(userHolding.toString());
            }}
            sx={{
              flex: 1,
              fontWeight: 600,
              fontSize: '0.75rem',
              py: 0.75,
              borderColor: 'divider',
              color: 'text.secondary',
              borderRadius: '6px',
              textTransform: 'none',
              '&:hover': {
                borderColor: 'primary.main',
                bgcolor: 'action.hover',
              },
            }}
          >
            {t('trade.max', { defaultValue: 'Max' })}
          </Button>
        </Box>
      )}
    </Box>
  );
}
